import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Download, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { MoodArt } from "@/components/art/Illustrations";
import { useCourtyard } from "@/context/CourtyardContext";
import { MOODS, copyText, inviteLink, moodById, shareText } from "@/lib/event";
import { canvasToBlob, renderInviteCanvas } from "@/lib/cardCanvas";
import { InviteCard } from "@/components/invite/InviteCard";
import { Reveal } from "@/components/invite/Reveal";

function MoodPicker({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="Festive mood" className="grid grid-cols-2 gap-4">
      {MOODS.map((m) => {
        const on = value === m.id;
        return (
          <motion.button key={m.id} type="button" role="radio" aria-checked={on} onClick={() => onChange(on ? null : m.id)} whileTap={{ scale: 0.95 }} animate={{ rotate: on ? -1.5 : 0, y: on ? -3 : 0 }}
            className={`pop-sm relative flex flex-col items-start p-4 text-left transition-colors duration-200 sm:p-5 ${on ? "bg-[var(--pink)] text-[var(--cream)] shadow-[5px_5px_0_#142344]" : "bg-white hover:bg-[var(--cream)]"}`}
            data-testid={`mood-card-${m.id}`}>
            {on && <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border-2 border-[var(--navy)] bg-[var(--yellow)] text-[var(--navy)]"><Check className="h-4 w-4" aria-hidden="true" /></span>}
            <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[var(--navy)] bg-[var(--cream)]"><MoodArt mood={m.id} className="h-12 w-12" /></span>
            <span className="font-poster mt-3 text-[clamp(0.95rem,4.3vw,1.15rem)] leading-[1.02] [overflow-wrap:anywhere] sm:text-2xl">{m.name}</span>
            <span className="mt-1 text-sm leading-snug">{m.line}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

function CardActions({ ev, name, mood, cardLink, accepted }) {
  const [busy, setBusy] = useState("");
  const file = async () => {
    const canvas = await renderInviteCanvas({ ev, name, mood, accepted });
    const blob = await canvasToBlob(canvas);
    const slug = (name || "guest").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "guest";
    return new File([blob], `diye-dost-aur-drama-${slug}.png`, { type: "image/png" });
  };
  const download = async () => {
    setBusy("download");
    try {
      const f = await file();
      const url = URL.createObjectURL(f);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Your pass is downloading");
    } catch {
      toast.error("Couldn't create the image. Please try again.");
    } finally {
      setBusy("");
    }
  };
  const copy = async () => ((await copyText(cardLink)) ? toast.success("Invitation link copied") : toast.error("Couldn't copy the link."));
  const share = async () => {
    const text = `${shareText(ev)} ${cardLink}`;
    setBusy("share");
    try {
      const f = await file().catch(() => null);
      if (f && navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], title: ev.title, text });
      else if (navigator.share) await navigator.share({ title: ev.title, text: shareText(ev), url: cardLink });
      else await copy();
    } catch (e) {
      if (e?.name !== "AbortError") await copy();
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="mt-7 flex flex-col gap-4">
      <button type="button" className="btn-pink w-full" onClick={download} disabled={!!busy} data-testid="download-card-button">
        <Download className="h-4 w-4" aria-hidden="true" /> {busy === "download" ? "Preparing…" : "Download my invitation"}
      </button>
      <div className="grid grid-cols-2 gap-4">
        <button type="button" className="btn-primary px-3" onClick={share} disabled={!!busy} data-testid="share-card-button"><Share2 className="h-4 w-4" aria-hidden="true" /> Share</button>
        <button type="button" className="btn-ghost px-3" onClick={copy} data-testid="copy-card-link-button"><Link2 className="h-4 w-4" aria-hidden="true" /> Copy link</button>
      </div>
    </div>
  );
}

export default function PassStudio() {
  const { event: ev, rsvp, style, updateStyle } = useCourtyard();
  const [name, setName] = useState(rsvp?.name || "");
  useEffect(() => {
    if (rsvp?.name) setName(rsvp.name);
  }, [rsvp?.name]);
  const mood = moodById(style.mood);
  const cardLink = rsvp?.card_id ? `${inviteLink(ev)}/card/${rsvp.card_id}` : inviteLink(ev);

  return (
    <div className="relative mx-auto mt-16 grid max-w-7xl gap-14 px-5 text-left sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-12" data-testid="pass-studio">
      <div className="lg:col-span-7">
        <Reveal>
          <h3 className="font-poster text-4xl leading-none sm:text-5xl">Apna <span className="font-serif-i text-[var(--pink-deep)]">pass</span></h3>
          <p className="mt-3 max-w-xl text-base">Optional. Pick a festive mood and add your name. Your pass updates as you choose.</p>
          <div className="mt-6"><MoodPicker value={style.mood} onChange={(v) => updateStyle({ mood: v })} /></div>
        </Reveal>
        <Reveal className="mt-10 max-w-md">
          <label htmlFor="card-name" className="font-extrabold">Name on your pass</label>
          <input id="card-name" className="field mt-2" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="card-name-input" />
          <p className="mt-2 text-sm">
            {rsvp ? "Your choices are saved with your RSVP." : "RSVP first and we’ll remember your choices on any device with your private link."}
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <div id="my-pass" className="scroll-mt-20 lg:sticky lg:top-20">
          <p className="eyebrow mb-4 text-center">Live preview</p>
          <InviteCard ev={ev} name={name.trim()} mood={mood} accepted={!!style.rules_accepted} />
          <CardActions ev={ev} name={name.trim()} mood={mood} cardLink={cardLink} accepted={!!style.rules_accepted} />
        </div>
      </div>
    </div>
  );
}
